import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip.tsx'
import { cn } from '#app/utils/misc.tsx'
import { redirect } from 'react-router'
import { type Route } from './+types/index.ts'
import { useOptionalUser } from '#app/utils/user.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { useTheme } from '#app/routes/resources+/theme-switch.tsx'
import {
	Card,
	CardHeader,
	CardTitle,
	CardContent,
} from '#app/components/ui/card.tsx'
import { prisma, isInstalled } from '#app/utils/db.server.ts'

export const meta: Route.MetaFunction = () => [{ title: 'PaideiaTrack' }]

export async function loader({ request }: Route.LoaderArgs) {
	try {
		await isInstalled()
		// Check if a user is logged in
		const userId = await getUserId(request)

		if (userId) {
			return redirect('/dashboard')
		}
		// App is installed and no user is logged in, so show the login page
		// return redirect('/login')
	} catch (error) {
		// If the SystemConfig table doesn't exist yet, it means the app isn't installed
		console.error('Error checking installation status:', error)
		return redirect('/install')
	}
}

export default function Index() {
	const user = useOptionalUser()
	const theme = useTheme()
	const isDarkTheme = theme === 'dark'

	// If user is logged in but somehow got here, redirect them
	if (user) {
		return <p className="text-center">Redirecting to dashboard...</p>
	}

	return (
		<main className="font-poppins grid h-full place-items-center">
			<div className="flex w-full max-w-5xl flex-col items-center px-4 py-12">
				{/* Logo at the top */}
				<div className="animate-fade-in mb-12 flex w-full justify-center">
					<img
						src={isDarkTheme ? '/img/logo-dark.webp' : '/img/logo.png'}
						alt="PaideiaTrack Logo"
						className="w-full max-w-md"
						onError={(e) => {
							// Fallback if specific theme image doesn't exist
							e.currentTarget.src = '/img/logo.webp'
						}}
					/>
				</div>

				{/* Main content */}
				<div className="w-full text-center">
					{/* Feature columns */}
					<div className="mt-16 grid w-full animate-slide-top grid-cols-1 gap-8 [animation-delay:0.9s] [animation-fill-mode:backwards] md:grid-cols-2">
						<Card className="bg-muted">
							<CardHeader>
								<CardTitle className="text-2xl font-semibold">
									For Teachers
								</CardTitle>
							</CardHeader>
							<CardContent>
								<ul className="space-y-4 text-left">
									<li className="flex items-start">
										<svg
											className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M5 13l4 4L19 7"
											></path>
										</svg>
										<span>
											Seamlessly login with your school Google credentials
										</span>
									</li>
									<li className="flex items-start">
										<svg
											className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M5 13l4 4L19 7"
											></path>
										</svg>
										<span>
											Automatic synchronization with FACTS class rosters
										</span>
									</li>
									<li className="flex items-start">
										<svg
											className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M5 13l4 4L19 7"
											></path>
										</svg>
										<span>Track and report student behavior infractions</span>
									</li>
									<li className="flex items-start">
										<svg
											className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M5 13l4 4L19 7"
											></path>
										</svg>
										<span>
											View behavioral patterns for individual students
										</span>
									</li>
								</ul>
							</CardContent>
						</Card>

						<Card className="bg-muted">
							<CardHeader>
								<CardTitle className="text-2xl font-semibold">
									For Administrators
								</CardTitle>
							</CardHeader>
							<CardContent>
								<ul className="space-y-4 text-left">
									<li className="flex items-start">
										<svg
											className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M5 13l4 4L19 7"
											></path>
										</svg>
										<span>Comprehensive reporting across all classes</span>
									</li>
									<li className="flex items-start">
										<svg
											className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M5 13l4 4L19 7"
											></path>
										</svg>
										<span>Review and approve recorded infractions</span>
									</li>
									<li className="flex items-start">
										<svg
											className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M5 13l4 4L19 7"
											></path>
										</svg>
										<span>
											Synchronize data with FACTS SIS for permanent records
										</span>
									</li>
									<li className="flex items-start">
										<svg
											className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-green-500"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M5 13l4 4L19 7"
											></path>
										</svg>
										<span>
											Generate insights on school-wide behavior trends
										</span>
									</li>
								</ul>
							</CardContent>
						</Card>
					</div>

					{/* Call to action buttons */}
					<div className="mt-12 flex animate-slide-top flex-col justify-center gap-4 [animation-delay:1.2s] [animation-fill-mode:backwards] sm:flex-row">
						<a
							href="/login"
							className="flex h-12 items-center justify-center rounded-md bg-primary px-8 text-base font-medium text-primary-foreground transition hover:bg-primary/90"
						>
							Login to PaideiaTrack
						</a>
						<a
							href="/about"
							className="flex h-12 items-center justify-center rounded-md border border-input bg-background px-8 text-base font-medium transition hover:bg-accent hover:text-accent-foreground"
						>
							Learn More
						</a>
					</div>

					{/* Login prompt */}
					<p className="mt-6 text-center text-sm text-muted-foreground">
						Sign in with your school Google account for easy access
					</p>
				</div>
			</div>
		</main>
	)
}
